import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DownloadSimple, Megaphone, Lock, Clock, ChartLineUp, ShoppingCart } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

export default function GrowthPlaybook() {
    const { user } = useAuth();
    const [playbooks, setPlaybooks] = useState([]);
    const [downloadingId, setDownloadingId] = useState(null);
    const [unlocking, setUnlocking] = useState(false);

    useEffect(() => {
        api.get("/playbooks").then((r) => setPlaybooks(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Intentionally run only on mount

    const downloadPdf = async (pb) => {
        if (!pb.pdf_file_id) return;
        setDownloadingId(pb.id);
        try {
            const r = await api.get(`/files/${pb.pdf_file_id}/download`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            const safe = pb.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
            a.download = `${safe}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("PLAYBOOK DOWNLOADED");
        } catch (err) {
            toast.error("Download failed");
        } finally {
            setDownloadingId(null);
        }
    };

    const unlockVisualAuthority = async () => {
        setUnlocking(true);
        try {
            const r = await api.post("/stripe/create-orderbump-checkout", {
                origin_url: window.location.origin,
            });
            window.location.href = r.data.url;
        } catch (err) {
            toast.error(err.response?.data?.detail || "CHECKOUT FAILED");
            setUnlocking(false);
        }
    };

    // Check if Visual Authority Guide is missing (locked for this user)
    const hasVisualAuthority = user?.lifetime_member || user?.purchased_visual_authority_guide;
    const visualAuthorityMissing = !hasVisualAuthority && playbooks.length > 0;

    return (
        <section data-testid="growth-playbook-section" className="bg-[#0A0A0A] border border-white/10 relative overflow-hidden">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Megaphone size={22} className="text-electric-blue" weight="duotone" />
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                    </div>
                    <div>
                        <p className="label-mono text-electric-blue">THE GROWTH PLAYBOOK</p>
                        <h2 className="font-heading font-bold text-lg">From 0 Views to 10K+</h2>
                    </div>
                </div>
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-emerald-500/30 bg-emerald-500/5">
                    <ChartLineUp size={12} className="text-emerald-400" weight="bold" />
                    <span className="label-mono text-[10px] text-emerald-400">DAY-1 FOUNDER BONUS</span>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-6">
                {/* Locked Visual Authority Card (if user hasn't purchased) */}
                {visualAuthorityMissing && (
                    <div
                        data-testid="visual-authority-locked"
                        className="bg-[#060606] border-2 border-yellow-400/50 transition-all group flex flex-col relative overflow-hidden"
                    >
                        <div className="aspect-[4/3] relative overflow-hidden bg-gradient-to-br from-yellow-400/20 to-black">
                            <img
                                src="https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?crop=entropy&cs=srgb&fm=jpg&q=85&w=940&h=650"
                                alt="Visual Authority Guide"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                            <div className="absolute top-3 left-3 px-2 py-1 bg-black/70 border border-yellow-400/50">
                                <span className="label-mono text-[9px] text-yellow-400">HEADLINES & THUMBNAILS</span>
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="px-3 py-2 bg-yellow-400/90 border border-yellow-500 flex items-center gap-2">
                                    <Lock size={14} weight="fill" className="text-black" />
                                    <span className="label-mono text-[10px] text-black font-bold">
                                        UNLOCK FOR $47
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 flex-1 flex flex-col">
                            <h3 className="font-heading font-bold text-base mb-2 line-clamp-2 text-yellow-300">
                                Strategy Guide #002: Visual Authority Guide
                            </h3>
                            <p className="text-xs text-muted-foreground mb-4 leading-relaxed line-clamp-3 flex-1">
                                The 'Mad-Lib' headline swipe file + Vegas-Tech thumbnail psychology. 
                                Engineer curiosity at the title bar and win the click in the feed.
                            </p>
                            <button
                                onClick={unlockVisualAuthority}
                                disabled={unlocking}
                                data-testid="unlock-visual-authority-btn"
                                className="inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-black font-bold px-3 py-2 text-xs label-mono transition-all disabled:opacity-50"
                            >
                                <ShoppingCart size={14} weight="fill" />
                                {unlocking ? "REDIRECTING..." : "UNLOCK NOW — $47"}
                            </button>
                        </div>
                    </div>
                )}

                {playbooks.map((p, i) => {
                    const isComingSoon = p.status === "coming_soon" || !p.pdf_file_id;
                    return (
                        <div
                            key={p.id}
                            data-testid={`playbook-card-${i}`}
                            className={`bg-[#060606] border border-white/10 transition-all group flex flex-col ${
                                isComingSoon ? "opacity-90" : "hover:border-electric-blue/60 hover:-translate-y-1"
                            }`}
                        >
                            <div className="aspect-[4/3] relative overflow-hidden bg-gradient-to-br from-electric-blue/20 to-black">
                                {p.cover_url && (
                                    <img
                                        src={p.cover_url}
                                        alt={p.title}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                        className={`w-full h-full object-cover transition-transform duration-500 ${
                                            isComingSoon ? "grayscale brightness-50" : "group-hover:scale-105"
                                        }`}
                                    />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                                <div className="absolute top-3 left-3 px-2 py-1 bg-black/70 border border-electric-blue/50">
                                    <span className="label-mono text-[9px] text-electric-blue">{p.category}</span>
                                </div>
                                {!isComingSoon && (
                                    <div className="absolute bottom-3 right-3 px-2 py-1 bg-electric-blue text-white">
                                        <span className="label-mono text-[10px]">UNLOCKED</span>
                                    </div>
                                )}
                                {isComingSoon && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="px-3 py-2 bg-black/85 border border-yellow-500/50 flex items-center gap-2">
                                            <Clock size={14} weight="fill" className="text-yellow-400" />
                                            <span className="label-mono text-[10px] text-yellow-400">
                                                {p.release_label || "COMING SOON"}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                <Megaphone
                                    size={56}
                                    weight="duotone"
                                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-electric-blue/15 pointer-events-none"
                                />
                            </div>
                            <div className="p-4 flex-1 flex flex-col">
                                <h3
                                    className={`font-heading font-bold text-base mb-2 line-clamp-2 ${
                                        isComingSoon ? "text-muted-foreground" : "group-hover:text-electric-blue transition-colors"
                                    }`}
                                >
                                    {p.title}
                                </h3>
                                <p className="text-xs text-muted-foreground mb-4 leading-relaxed line-clamp-3 flex-1">
                                    {p.description}
                                </p>
                                {isComingSoon ? (
                                    <div
                                        data-testid={`playbook-locked-${i}`}
                                        className="inline-flex items-center justify-center gap-2 bg-yellow-500/5 border border-yellow-500/30 text-yellow-400 px-3 py-2 text-xs label-mono"
                                    >
                                        <Lock size={12} weight="fill" />
                                        FOUNDER EARLY ACCESS
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => downloadPdf(p)}
                                        disabled={downloadingId === p.id}
                                        data-testid={`playbook-download-${i}`}
                                        className="inline-flex items-center justify-center gap-2 bg-transparent border border-electric-blue text-electric-blue hover:bg-electric-blue hover:text-white px-3 py-2 text-xs label-mono transition-all disabled:opacity-50"
                                    >
                                        <DownloadSimple size={14} weight="bold" />
                                        {downloadingId === p.id ? "DOWNLOADING..." : "DOWNLOAD GUIDE"}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
                {playbooks.length === 0 && !visualAuthorityMissing && (
                    <div className="col-span-full p-8 text-center">
                        <Megaphone size={32} className="text-muted-foreground mx-auto mb-3" />
                        <p className="label-mono text-muted-foreground">PLAYBOOK INITIALIZING...</p>
                    </div>
                )}
            </div>
        </section>
    );
}
